import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNodeData, useCollaborativeState } from '@/hooks/collaboration'
import { useAICommandState, AICommandType } from '@/utils/aiCommandUtils'
import type {
    NodeJavascripterData,
    JavascriptDialogStates,
    IntroState,
    ExecutionState,
    CodeInputState,
    NodeConfiguration,
    NodeDataSettings
} from '../types'

/**
 * Hook for managing Javascript Node state following the syncNodeData pattern.
 * Handles all state management for the javascript node component.
 */
export function useJavascriptNodeState(
    nodeId: string,
    initialData: NodeJavascripterData,
    updateNodeData: (id: string, updater: (prevData: any) => any) => void
) {
    // Collaborative state management
    const { syncNodeData, hasExternalChange, updateLastSyncedData } = useNodeData(
        nodeId,
        updateNodeData,
        false // Set to true for debugging
    )

    // Collaborative state for persistent data
    const persistedCode = useCollaborativeState(
        'javascriptCommand.code',
        '',
        initialData.javascriptCommand?.code,
        syncNodeData
    )

    const persistedRequirements = useCollaborativeState(
        'javascriptCommand.requirements',
        '',
        initialData.javascriptCommand?.requirements,
        syncNodeData
    )

    const persistedContext = useCollaborativeState(
        'javascriptCommand.context',
        '',
        initialData.javascriptCommand?.context,
        syncNodeData
    )

    const linkedKnowledge = useCollaborativeState(
        'linkedKnowledge',
        '',
        initialData.linkedKnowledge,
        syncNodeData
    )

    const assignedEmails = useCollaborativeState(
        'assignedEmails',
        '',
        initialData.assignedEmails,
        syncNodeData
    )

    // Local UI state (immediate input responsiveness)
    const [codeInput, setCodeInput] = useState(initialData.javascriptCommand?.code || '')
    const [requirementsInput, setRequirementsInput] = useState(initialData.javascriptCommand?.requirements || '')
    const [contextInput, setContextInput] = useState(initialData.javascriptCommand?.context || '')
    const [command, setCommand] = useState(initialData.javascriptCommand?.command || 'Generate')

    // Processing and status state
    const [isProcessing, setIsProcessing] = useState(false)
    const [statusMessage, setStatusMessage] = useState('')

    // UI state
    const [isLocked, setIsLocked] = useState(initialData.isLocked || false)
    const [isMinimized, setIsMinimized] = useState(initialData.isMinimized || false)
    const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(
        initialData.minimizeLevel || 'normal'
    )
    const [showDebugInfo, setShowDebugInfo] = useState(false)

    // Configuration state
    const [nodeConfig, setNodeConfig] = useState<NodeConfiguration>(
        initialData.configuration || { isAutomated: false, modifierprompt: '' }
    )

    const [dataSettings, setDataSettings] = useState<NodeDataSettings>(
        initialData.dataSettings || {
            inputLinks: [],
            outputLinks: [],
            availableNodes: [],
            exposedData: {},
            autoSync: false,
        }
    )

    // Dialog states
    const [dialogStates, setDialogStates] = useState<JavascriptDialogStates>({
        configDialog: false,
        dataSettingsDialog: false,
        introDialog: false
    })

    // Intro.js state
    const [introState, setIntroState] = useState<IntroState>({
        enabled: false,
        steps: [],
        stepsIndex: 0
    })

    // Sync local input state with collaborative state when it changes externally
    useEffect(() => {
        const fieldsToCheck = ['javascriptCommand', 'isLocked', 'isMinimized', 'minimizeLevel', 'configuration', 'dataSettings']
        if (hasExternalChange(initialData, fieldsToCheck)) {
            setCodeInput(initialData.javascriptCommand?.code || '')
            setRequirementsInput(initialData.javascriptCommand?.requirements || '')
            setContextInput(initialData.javascriptCommand?.context || '')
            setCommand(initialData.javascriptCommand?.command || 'Generate')
            setIsLocked(initialData.isLocked || false)
            setIsMinimized(initialData.isMinimized || false)
            setMinimizeLevel(initialData.minimizeLevel || 'normal')
            setNodeConfig(initialData.configuration || { isAutomated: false, modifierprompt: '' })
            setDataSettings(initialData.dataSettings || {
                inputLinks: [],
                outputLinks: [],
                availableNodes: [],
                exposedData: {},
                autoSync: false,
            })
            updateLastSyncedData(initialData)
        }
    }, [initialData, hasExternalChange, updateLastSyncedData])    // Use AI command state management for consistent behavior
    const {
        isProcessing: aiIsProcessing,
        statusMessage: aiStatusMessage,
        nodeStatus: aiNodeStatus,
    } = useAICommandState(initialData.javascriptCommand, AICommandType.JAVASCRIPT_COMMAND)

    // Computed execution state using AI command utilities
    const executionState: ExecutionState = useMemo(() => ({
        isProcessing: aiIsProcessing || isProcessing,
        statusMessage: aiStatusMessage || statusMessage,
        hasResult: !!initialData.javascriptCommand?.result,
        hasError: !!initialData.javascriptCommand?.error,
        hasExecutionResult: !!initialData.javascriptCommand?.executionResult,
        isInProgress: aiIsProcessing,
    }), [aiIsProcessing, aiStatusMessage, isProcessing, statusMessage, initialData.javascriptCommand])

    // Code input state getter
    const codeInputState: CodeInputState = {
        codeInput,
        requirementsInput,
        contextInput,
        command
    }

    // Dialog management functions
    const openDialog = useCallback((dialogName: keyof JavascriptDialogStates) => {
        setDialogStates(prev => ({ ...prev, [dialogName]: true }))
    }, [])

    const closeDialog = useCallback((dialogName: keyof JavascriptDialogStates) => {
        setDialogStates(prev => ({ ...prev, [dialogName]: false }))
    }, [])

    // Input change handlers (local state for immediate response)
    const handleCodeChange = useCallback((value: string) => {
        setCodeInput(value)
    }, [])

    const handleRequirementsChange = useCallback((value: string) => {
        setRequirementsInput(value)
    }, [])

    const handleContextChange = useCallback((value: string) => {
        setContextInput(value)
    }, [])

    const handleCommandChange = useCallback((value: string) => {
        setCommand(value)
    }, [])

    // Blur handlers (sync to collaborative state)
    const handleCodeBlur = useCallback(() => {
        persistedCode.updateValue(codeInput, true)
    }, [persistedCode, codeInput])

    const handleRequirementsBlur = useCallback(() => {
        persistedRequirements.updateValue(requirementsInput, true)
    }, [persistedRequirements, requirementsInput])

    const handleContextBlur = useCallback(() => {
        persistedContext.updateValue(contextInput, true)
    }, [persistedContext, contextInput])

    // Action handlers
    const handleLock = useCallback(() => {
        const newState = !isLocked
        setIsLocked(newState)
        syncNodeData({ isLocked: newState })
    }, [isLocked, syncNodeData])

    const handleMinimize = useCallback(() => {
        const newState = !isMinimized
        setIsMinimized(newState)
        syncNodeData({ isMinimized: newState })
    }, [isMinimized, syncNodeData])

    const handleMinimizeLevelChange = useCallback((level: 'normal' | 'medium' | 'minimum') => {
        setMinimizeLevel(level)
        syncNodeData({ minimizeLevel: level })
    }, [syncNodeData])

    const handleConfigChange = useCallback((config: NodeConfiguration) => {
        setNodeConfig(config)
        syncNodeData({ configuration: config })
    }, [syncNodeData])

    const handleDataSettingsChange = useCallback((settings: NodeDataSettings) => {
        setDataSettings(settings)
        syncNodeData({ dataSettings: settings })
    }, [syncNodeData])

    const handleLinkedKnowledgeChange = useCallback((value: string) => {
        linkedKnowledge.setValue(value)
    }, [linkedKnowledge])

    const handleLinkedKnowledgeBlur = useCallback(() => {
        linkedKnowledge.syncToCollaborative()
    }, [linkedKnowledge])

    const handleAssignedEmailsChange = useCallback((emails: string) => {
        assignedEmails.updateValue(emails, true)
    }, [assignedEmails])

    // Processing handlers
    const handleStartProcessing = useCallback((message = 'Processing...') => {
        setIsProcessing(true)
        setStatusMessage(message)
    }, [])

    const handleStopProcessing = useCallback((message = '') => {
        setIsProcessing(false)
        setStatusMessage(message)
    }, [])

    // Intro tour handlers
    const handleStartIntro = useCallback((steps: any[]) => {
        setIntroState({
            enabled: true,
            steps,
            stepsIndex: 0
        })
    }, [])

    const handleStopIntro = useCallback(() => {
        setIntroState(prev => ({ ...prev, enabled: false }))
    }, [])

    return {
        // Collaborative state values
        persistedCode: persistedCode.value,
        persistedRequirements: persistedRequirements.value,
        persistedContext: persistedContext.value,
        linkedKnowledge: linkedKnowledge.value,
        assignedEmails: assignedEmails.value,

        // Local UI state
        codeInputState,
        isLocked,
        isMinimized,
        minimizeLevel,
        showDebugInfo,
        nodeConfig,
        dataSettings,

        // Computed state
        executionState,

        // Dialog states
        dialogStates,
        introState,

        // State setters
        setShowDebugInfo,
        setIntroState,

        // Dialog management
        openDialog,
        closeDialog,

        // Input handlers
        handleCodeChange,
        handleRequirementsChange,
        handleContextChange,
        handleCommandChange,
        handleCodeBlur,
        handleRequirementsBlur,
        handleContextBlur,

        // Action handlers
        handleLock,
        handleMinimize,
        handleMinimizeLevelChange,
        handleConfigChange,
        handleDataSettingsChange,
        handleLinkedKnowledgeChange,
        handleLinkedKnowledgeBlur,
        handleAssignedEmailsChange,

        // Processing handlers
        handleStartProcessing,
        handleStopProcessing,

        // Intro handlers
        handleStartIntro,
        handleStopIntro,

        // Collaboration utilities
        syncNodeData,
        hasUnsyncedChanges: (
            persistedCode.hasUnsyncedChanges ||
            persistedRequirements.hasUnsyncedChanges ||
            persistedContext.hasUnsyncedChanges ||
            linkedKnowledge.hasUnsyncedChanges ||
            assignedEmails.hasUnsyncedChanges
        )
    }
}
